"use client";

import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import moment from "moment";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popOver";
import { cn } from "@/lib/utils";
import { fieldVariantClasses } from "./field-variants";

export interface DatePickerV2Props {
  value?: Date | null;
  onChange?: (date: Date | null, event?: unknown) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: Date;
  max?: Date;
  error?: string;
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  placement?: string;
  onBlur?: () => void;
  name?: string;
  shouldDisableDate?: (date: Date) => boolean;
  calendarDefaultDate?: Date;
  variant?: "violet" | "sky" | "amber" | "emerald" | "rose" | "slate" | "subtle";
  "aria-label"?: string;
}

const DatePickerV2 = ({
  value,
  onChange,
  placeholder = "DD/MM/YYYY",
  disabled = false,
  min,
  max,
  error,
  label,
  className,
  variant = "subtle",
  "aria-label": ariaLabel,
}: DatePickerV2Props) => {
  const [open, setOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const generatedId = React.useId();
  const inputId = generatedId;
  const isFloating = Boolean(label) || isFocused || Boolean(value);
  const colors = fieldVariantClasses(variant, { error: Boolean(error) });

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="relative w-full">
          <PopoverTrigger asChild>
            <button
              id={inputId}
              type="button"
              aria-label={ariaLabel}
              disabled={disabled}
              className={cn(
                "peer flex w-full items-center gap-2 rounded-2xl border bg-white/70 text-xs leading-4 shadow-[0_1px_2px_rgba(15,23,42,0.05)] outline-none transition-all duration-200 dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]",
                "h-10 px-3",
                "focus:outline-none focus:ring-1 focus:ring-offset-0",
                colors.border,
                colors.focusBorder,
                colors.focusRing,
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              {value ? (
                <span className="text-foreground">
                  {moment(value).format("DD/MM/YYYY")}
                </span>
              ) : label ? (
                <span aria-hidden="true" className="text-transparent">00/00/0000</span>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </button>
          </PopoverTrigger>

          {label && !open && (
            <label
              htmlFor={inputId}
              className={cn(
                "pointer-events-none absolute z-20 select-none font-medium transition-all duration-200",
                "left-3",
                isFloating
                  ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[10px] leading-none dark:bg-zinc-950/90"
                  : "top-1/2 -translate-y-1/2 cursor-text text-xs text-muted-foreground",
                error && isFloating && "text-destructive",
              )}
            >
              {label}
            </label>
          )}
        </div>

        <PopoverContent
          className={cn(
            "w-auto rounded-2xl p-0 border-slate-200/80 bg-white/95 dark:border-zinc-800 dark:bg-zinc-950/95",
            "shadow-[0_12px_30px_-18px_rgba(15,23,42,0.35)] backdrop-blur-sm dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          align="start"
          style={{ zIndex: 12001 }}
        >
          <Calendar
            mode="single"
            selected={value ?? undefined}
            defaultMonth={value ?? undefined}
            onSelect={(d) => {
              onChange?.(d ?? null);
              setOpen(false);
            }}
            captionLayout="dropdown"
            className="p-4"
            disabled={(date) =>
              Boolean((min && date < min) || (max && date > max))
            }
          />
        </PopoverContent>
      </Popover>

      {error && (
        <p className="mt-1.5 px-1 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
};

DatePickerV2.displayName = "DatePickerV2";

export { DatePickerV2 };
